// The goal of the project is fast scoring of your brain energy and sharpness by solving chess puzzles. 
// Motivation: I wanted to create a simple and fast way to measure my brain energy and sharpness during the day.
// Absolute rating is not important, only the trend is important.

<template>
  <div class="puzzle-container flex flex-col items-center">
    <!-- Header -->
    <h1 class="text-3xl font-extrabold mb-2">Cognitive Score: Chess Metric</h1>

    <div class="my_chessboard w-full relative">
      <TheChessboard
        class="chessboard"
        :board-config="boardConfig"
        @board-created="boardCreated"
        @move="onMove"
      />
      <div v-if="paused" class="overlay" @click="togglePause">
        <div class="play-button"></div>
      </div>
    </div>

    <div class="puzzle-top">
      <div class="puzzle-rating">
        <span class="puzzle-rating-label">Puzzle rating:</span>
        <span class="puzzle-rating-value">{{ puzzle?.Rating }}</span>
      </div>

      <svg
        class="progress-ring"
        width="60"
        height="60"
        style="cursor: pointer"
        @click="togglePause"
      >
        <circle
          class="progress-ring__circle"
          stroke="green"
          stroke-width="4"
          fill="transparent"
          :r="radius"
          cx="30"
          cy="30"
        />
        <!-- Pause button -->
        <g class="pause-button">
          <rect y="22" x="20" width="20" height="4" fill="green"></rect>
          <rect y="34" x="20" width="20" height="4" fill="green"></rect>
        </g>
      </svg>
    </div>

    <div class="info_block my-10">
      <div class="user-rating">
        <span class="user-rating-label">My rating:</span>
        <span class="user-rating-value">{{ userRating }}</span>
        <span class="user-rating-deviation">±{{ userMyRatingStd }}</span>
      </div>

      <div v-if="historyOfRatings.length > 0" class="history m-b-5">
        <h1 class="text-xl font-extrabold">History</h1>
        <table class="history-table">
          <tr v-for="(rating, index) in historyOfRatings" :key="rating.time">
            <td>{{ formatDate(rating.time) }}</td>
            <td>{{ rating.rating }}</td>
            <td>±{{ rating.deviation }}</td>
            <td>
              <input
                type="text"
                v-model="rating.comment"
                @blur="saveComment()"
                placeholder="✏️"
              />
            </td>
            <td>
              <button @click="deleteHistoryEntry(index)">✖</button>
            </td>
          </tr>
        </table>
      </div>
    </div>
    <div class="explanation info_block my-10">
      <div
        class="explanation-block"
        @click="isGoalCollapsed = !isGoalCollapsed"
      >
        <h1 class="text-xl font-extrabold mb-2">
          Goal of the Chess Puzzle Page
          <span v-if="isGoalCollapsed">▼</span>
          <span v-else>▲</span>
        </h1>
        <div v-if="!isGoalCollapsed">
          <p>
            The Chess Puzzle page is designed to measure and track cognitive
            sharpness and brain energy through the challenge of solving chess
            puzzles. It is a tool for users to assess their mental acuity at
            different times of the day.
          </p>

          <h2 class="text-xl font-extrabold my-2">Rules and Features</h2>
          <ul>
            <li>
              <strong>Chess Puzzle Solving:</strong> Solve chess puzzles within
              a set time limit to test your cognitive skills.
            </li>
            <li>
              <strong>Rating System:</strong> Each puzzle has a difficulty
              rating, and users have their own ratings that adjust based on
              performance.
            </li>
            <li>
              <strong>Timer:</strong> A countdown timer adds urgency to the
              puzzle-solving experience.
            </li>
            <li>
              <strong>User Performance Tracking:</strong> Track your rating
              changes and rating deviation to monitor your progress over time.
            </li>
            <li>
              <strong>History:</strong> A historical record of your ratings is
              maintained to help you see trends in your performance.
            </li>
            <li>
              <strong>Adaptive Difficulty:</strong> Puzzles are matched to your
              rating level to provide a consistent challenge.
            </li>
            <li>
              <strong>Local Storage:</strong> Your rating history is stored
              locally to maintain progress across sessions.
            </li>
            <li>
              <strong>Glicko-2 Rating System:</strong> The user's rating is
              updated using the Glicko-2 system, reflecting changes in skill
              level after each puzzle.
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div v-if="showCheckmark" class="checkmark-emoji">&#x2714;</div>
  </div>
  <div class="made-by mb-2">
    <a href="https://t.me/lovesyuk">Made by lovesyuk 💜</a>
  </div>
</template>


<script setup>
import { TheChessboard } from "vue3-chessboard";
import "vue3-chessboard/style.css";
import { ref, onMounted } from "vue";
import { Glicko2 } from "glicko2";

const apiUrl = "https://chess.pomodorr.io/get_puzzle";

const puzzle = ref(null);
const historyOfRatings = ref([]);
const showCheckmark = ref(false);
const boardConfig = {};
const isGoalCollapsed = ref(true);
const paused = ref(false);

const timeLeft = ref(10);
let countdownInterval = null;

let boardAPI = null;
let movesArray = [];
let sideToPlay = "w";

// Create a Glicko-2 player for the user
let userRating = ref(null);
let userGlickoRatingStd = ref(null);
// My rating std
let sessionRatingHistory = [];
let userMyRatingStd = ref(null);

let glicko = null;

const initBoard = (FEN, moves) => {
  boardAPI.setPosition(FEN);

  const lastMoveSide = FEN.split(" ")[1];
  sideToPlay = lastMoveSide === "w" ? "b" : "w";
  movesArray = moves.split(" ");

  let curr_board_orientation = boardAPI.board.state.orientation;
  curr_board_orientation = curr_board_orientation === "white" ? "w" : "b";

  if (sideToPlay !== curr_board_orientation) {
    boardAPI.toggleOrientation();
  }
};

const fetchPuzzle = async () => {
  try {
    const response = await fetch(`${apiUrl}?rating=${userRating.value}`);
    const data = await response.json();
    puzzle.value = data;
    // Initialize the board with the new puzzle

    initBoard(data.FEN, data.Moves);

    setTimeout(() => {
      if (boardAPI !== null) {
        boardAPI.move(movesArray[0]);
      }
    }, 300);
    resetTimer();
  } catch (error) {
    console.error("Error fetching puzzle:", error);
  }
};

const radius = 26; // The radius of the circle
const circumference = 2 * Math.PI * radius; // Calculate the circumference

// Create a method to start the countdown
const startCountdown = () => {
  timeLeft.value = 10; // Reset the countdown to 10 seconds
  countdownInterval = setInterval(() => {
    if (!paused.value && timeLeft.value > 0) {
      timeLeft.value -= 0.1;

      const circle = document.querySelector(".progress-ring__circle");
      if (circle) {
        const offset = circumference - (timeLeft.value / 10) * circumference;
        circle.style.strokeDashoffset = offset.toString();
        circle.style.strokeDasharray = circumference.toString();
      }
    }

    if (timeLeft.value <= 0) {
      clearInterval(countdownInterval);
      updateUserRating(false);
      fetchPuzzle();
    }
  }, 100);
};

// Clear the interval when the puzzle is reset
const resetTimer = () => {
  clearInterval(countdownInterval);
  startCountdown();
};

const togglePause = () => {
  paused.value = !paused.value;
};

const boardCreated = (api) => {
  boardAPI = api;
};

const onMove = (move) => {
  if (boardAPI.game.turn() === sideToPlay) {
    return;
  }
  // console.log(boardAPI);
  let lastMoveIx = boardAPI.game.history().length - 1;

  // check players move
  if (move.lan === movesArray[lastMoveIx]) {
    console.log("correct move");
    // make computer move
    if (lastMoveIx + 1 >= movesArray.length) {
      console.log("Success!");
      showCheckmarkTimeout();
      updateUserRating(true);
      fetchPuzzle();
      return;
    }
    setTimeout(() => {
      boardAPI.move(movesArray[lastMoveIx + 1]);
    }, 300);
    // reset timerToNewPuzzle to 10 sec
    resetTimer();
  } else {
    console.log("wrong move");
    setTimeout(() => {
      boardAPI.undoLastMove();
    }, 300);
    updateUserRating(false);
    fetchPuzzle();
  }
};

// Function to update user rating
const updateUserRating = (puzzleSolved) => {
  let newRating = glicko.makePlayer(
    userRating.value,
    userGlickoRatingStd.value
  );

  // Set the result of the match (1 = win, 0.5 = draw, 0 = loss)
  const result = puzzleSolved ? 1 : 0;

  let puzzleRating = glicko.makePlayer(
    puzzle.value.Rating,
    puzzle.value.RatingDeviation
  );

  let match = [[newRating, puzzleRating, result]];

  glicko.updateRatings(match);

  // Update user's displayed rating
  userRating.value = Math.round(newRating.getRating());
  userGlickoRatingStd.value = Math.round(newRating.getRd());
  saveUserRating();

  // calc my rating std
  sessionRatingHistory.push(userRating.value);
  updateMyStd();
};

const updateMyStd = () => {
  // take last 90% of history
  let min_ix = Math.floor(sessionRatingHistory.length * 0.1);
  min_ix = Math.max(min_ix, sessionRatingHistory.length - 50);
  let shortHistory = sessionRatingHistory.slice(min_ix);
  let mean = shortHistory.reduce((a, b) => a + b, 0) / shortHistory.length;
  let variance = shortHistory.reduce((a, b) => a + Math.pow(b - mean, 2), 0);
  let std = Math.sqrt(variance / shortHistory.length);

  userMyRatingStd.value = Math.round(std + 200 / (shortHistory.length + 1));
};

onMounted(() => {
  loadRatingsHistory();
  initRatings();
  fetchPuzzle();
});

const initRatings = () => {
  let userVolatility = 0.06;

  glicko = new Glicko2({
    tau: 0.5,
    rating: 1500,
    rd: 200,
    vol: userVolatility,
  });

  // try load user rating from localStorage
  const history = JSON.parse(localStorage.getItem("history")) || [];
  if (history.length > 0) {
    const lastRating = history[history.length - 1];
    userRating.value = lastRating.rating;
    userGlickoRatingStd.value = Math.min(lastRating.deviation * 2, 200); // set high deviation to allow fast rating changes
    userGlickoRatingStd.value = Math.max(userGlickoRatingStd.value, 50);
  } else {
    userRating.value = 500;
    userGlickoRatingStd.value = 200;
  }
  userMyRatingStd.value = userGlickoRatingStd.value;
  sessionRatingHistory.push(userRating.value);
};

// save User rating, deviation and current time as point in measurement history to localStorage
const saveUserRating = () => {
  const FIVE_MINUTES = 5 * 60;
  const data = {
    rating: Math.round(userRating.value),
    deviation: Math.round(userMyRatingStd.value),
    time: Math.round(Date.now() / 1000),
  };

  let history = JSON.parse(localStorage.getItem("history")) || [];

  if (
    history.length === 0 ||
    data.time - history[history.length - 1].time > FIVE_MINUTES
  ) {
    history.push(data);
  } else {
    history[history.length - 1] = data;
  }

  localStorage.setItem("history", JSON.stringify(history));
  loadRatingsHistory();
};

const formatDate = (timestamp) => {
  const date = new Date(timestamp * 1000);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const saveHistory = () => {
  // Update localStorage with the new history
  localStorage.setItem(
    "history",
    JSON.stringify(historyOfRatings.value.reverse())
  );
  loadRatingsHistory();
};

const saveComment = () => {
  saveHistory();
};

const deleteHistoryEntry = (index) => {
  // Remove the entry from the array
  historyOfRatings.value.splice(index, 1);
  // Update localStorage with the new history
  saveHistory();
};

const loadRatingsHistory = () => {
  let history = JSON.parse(localStorage.getItem("history")) || [];
  historyOfRatings.value = history.sort((a, b) => b.time - a.time);
};

const showCheckmarkTimeout = () => {
  showCheckmark.value = true;
  setTimeout(() => {
    showCheckmark.value = false;
  }, 1000);
};
</script>



<style>
.puzzle-container {
  max-width: 600px;
  margin: 0 auto;
}
.chessboard {
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  width: 100%;
  margin-bottom: 5px;
}

.checkmark-emoji {
  position: absolute;
  top: 50%;
  left: 50%;
  font-size: 10rem;
  animation: fadeOut 2s forwards;
  transform: translate(-50%, -50%); /* Center the checkmark */
  color: green;
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

.info_block {
  padding: 20px;
  margin: 20px auto;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  background-color: #f9f9f9;
  max-width: 600px;
  text-align: left;
  width: 100%;
}

.history-table {
  width: 100%;
}

.history-table tr {
  border-bottom: 1px solid #eaeaea;
}

.history-table td {
  padding-right: 20px;
  text-align: left;
}

.history-table button {
  margin-left: 10px;
  padding: 4px 8px;
  color: #e74c3c;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.history-table button:hover {
  background-color: #e74c3c;
  color: white;
}

.history-table input[type="text"] {
  padding: 8px 4px; /* Increase padding for better touch area */
  /* remove background, border and outline */
  background: none;
  border: none;
  outline: none;
  /* make the font size and color the same as the table */
  font-size: 1rem;
  /* limit the width of the input to 100% of the cell */
  width: 100%;
}

.puzzle-rating {
  margin-bottom: 0.5rem;
  margin-left: 2rem;
}

.puzzle-rating-label {
  font-size: 2rem;
  font-weight: bold; /* Make the label bold */
  margin-right: 0.5rem; /* Spacing between the label and value */
}

.puzzle-rating-value {
  font-size: 2rem;
  font-weight: normal; /* Normal font weight for the value */
}

.user-rating {
  margin-bottom: 1rem;
}

.user-rating-label {
  font-size: 2rem;
  font-weight: bold;
}

.user-rating-value {
  font-size: 2rem;
  padding-left: 2rem;
  padding-right: 0.25rem;
}

.user-rating-deviation {
  font-size: 1.5rem;
  color: #7f8c8d;
}

/* Responsive design for mobile devices */
@media (max-width: 600px) {
  .puzzle-container {
    padding: 5px; /* Reduce padding on smaller screens */
  }

  .chessboard {
    width: 100%; /* Reduce width to provide padding around the board */
    margin-bottom: 10px; /* Reduce space below the chessboard */
  }
}

.progress-ring {
  transform: rotate(
    -90deg
  ); /* Rotate the progress ring to start from the top */
}

.progress-ring__circle {
  transition: stroke-dashoffset 0.35s;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
}

.puzzle-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
li {
  margin-bottom: 10px;
}

.explanation-block:hover {
  cursor: pointer;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10; /* Ensure it's above other content */
  backdrop-filter: blur(20px); /* Apply a blur effect */
  cursor: pointer;
}

.play-button {
  width: 0;
  height: 0;
  border-top: 30px solid transparent; /* Adjust size as needed */
  border-bottom: 30px solid transparent; /* Adjust size as needed */
  border-left: 60px solid #fff; /* Change color if needed, this is the color of the triangle */
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.play-button:hover {
  border-left-color: #ccc;
}
</style>